import { put, takeLeading, select, call } from 'redux-saga/effects';
import _ from 'lodash';
import notifyBugsnag from '../../../../../util/notifyBugsnag';

import Notification from '../../../../../components/layout/Notification';
import { formatCartForApi } from '../../../../../util/Helpers';
import * as orderActions from '../../../reducers/ducks/legacy/OrderDuck';
import * as mainActions from '../../../reducers/ducks/legacy/MainDuck';
import {
  checkoutAdjust as restaurantApiCheckoutAdjust,
  checkoutEnd as restaurantApiCheckoutEnd,
  checkoutStart as restaurantApiCheckoutStart,
  getOrderCart,
  resetOrderCart,
  setOrderCart,
} from '../../../../../util/api/RestaurantApiClient';
import { getTimeParams } from '../../../../../util/Restaurant';
import safeJsonParse from '../../../../../util/safeJsonParse';
import findMatchingAddress from '../../../../../util/addressMatch';
import errorMessages from '../../../../../constants/errorMessages';
import accountApi from '../../../../../util/api/AccountApiClient';
import { getAddressLocation } from '../../../../../util/api/MapApiClient';
import {
  setCartResponse,
  updateCartStatus,
} from '../../../reducers/ducks/legacy/OrderDuck';
import { setSelectedAddress } from '../../../reducers/ducks/new/Address/AddressReducer';
import { setGCAvailableTrigger } from '../../../reducers/ducks/legacy/GiftDuck';
import { updatePhones } from '../../../reducers/ducks/legacy/ProfileDuck';

/**
 * @returns {Array<{
 *   menuItemId: number;
 *   sort: number;
 *   quantity: number;
 *   comment: string;
 *   extras: {
 *     extraId: {
 *       id: number;
 *       items: Array<{
 *         id: number;
 *         quantity: number;
 *       }>;
 *     };
 *   };
 * }>}
 */
const formatCartForStore = (apiCart, menuItems) => {
  const cart = [];

  apiCart.forEach((cartItem) => {
    const menuItem = menuItems[cartItem.id];
    const items = cartItem.items.map((item) => {
      const extras = {};
      (item.answers || []).forEach((answer) => {
        const extraItem = {
          id: answer.answer,
          quantity: answer.amount || 1,
          extraId: null,
          parentId: null,
          sort: menuItem.options[answer.answer]?.sort,
        };

        if (answer.parent) {
          extraItem.id = answer.parent;
          extraItem.parentId = answer.answer;
          extraItem.sort = menuItem.options[answer.parent]?.sort;
        }

        const extra = menuItem.extras[menuItem.options[extraItem.id].parent];
        extraItem.extraId = extra.id;
        extraItem.order = extra.order;

        extras[extraItem.id] = extraItem;
      });

      return {
        menuItemId: cartItem.id,
        sort: item.sort,
        quantity: item.quantity || 1,
        comment: item.special_instructions || '',
        extras,
      };
    });

    cart.push(...items);
  });

  return cart.sort((a, b) => (a.sort > b.sort ? 1 : -1));
};

export function* checkoutAdjustSaga({ payload }) {
  const { selectedRestaurant, checkoutData, coupons } = yield select(
    ({ restaurant, order }) => ({
      selectedRestaurant: restaurant.selectedRestaurantId,
      checkoutData: order.checkoutData,
      coupons: order.coupons,
    })
  );

  try {
    const clientId = selectedRestaurant || checkoutData?.restaurant;

    const requestBody = {
      client: clientId,
      checkout: checkoutData?.id,
      data: payload,
    };

    const newCheckoutData = yield call(
      restaurantApiCheckoutAdjust,
      requestBody
    );

    yield put(
      orderActions.setCheckoutData({
        ...checkoutData,
        ...newCheckoutData,
      })
    );

    yield put(orderActions.setOrderFees(newCheckoutData.fees));
    if (!_.isEmpty(coupons[0])) {
      yield put(
        orderActions.setActiveCoupon({ isValid: true, coupon: coupons[0] })
      );
    }

    yield put(orderActions.setCheckoutAdjustResponse(newCheckoutData));
  } catch (err) {
    const errorMessage = err?.data?.message || err?.message;

    yield put(
      orderActions.setCheckoutAdjustResponse({
        error: true,
        errorMessage,
      })
    );

    notifyBugsnag(err);

    Notification.error(errorMessage || 'Error while adjusting the checkout', {
      toastId: 'ErrorWhileAdjustingTheCheckout',
    });
  }
}

export function* checkoutStart() {
  try {
    const {
      selectedRestaurant,
      cartItems,
      type,
      time,
      address,
      phone,
      phones,
      user,
      savedAddresses,
    } = yield select(
      ({
        order,
        restaurant,
        profile,
        address: selectedAddress,
        time: selectedTime,
        main,
      }) => ({
        selectedRestaurant: restaurant?.selectedRestaurantId,
        checkoutData: order.checkoutData,
        cartItems: order.cartItems,
        type: order.type,
        time: selectedTime.selectedTime,
        address: selectedAddress.selectedAddress,
        phone: order.phone,
        phones: profile.phones,
        user: main.user,
        savedAddresses: profile.getAddressesResponse?.response || [],
      })
    );

    const cart = formatCartForApi(cartItems);

    if (!cart || !cart.length) {
      yield put(
        mainActions.toggleGlobalLoaderQueue({ name: 'checkout', status: false })
      );
      Notification.error(
        'Your cart is empty. Please add items before checking out.',
        { toastId: 'EmptyCartError' }
      );
      return;
    }

    let phoneId;

    if (phone) {
      phoneId = phones?.find((i) => i.number === phone)?.id;
    }

    if (!phoneId) {
      const fallbackPhone = phones?.find((i) => i.primary) ?? phones?.[0];
      phoneId = fallbackPhone?.id;
      yield put(orderActions.setPhone(fallbackPhone?.number));
    }

    if (!phoneId) {
      const refreshedPhones = yield call(accountApi.phone.getAllPhones);
      if (refreshedPhones?.length > 0) {
        const refreshed =
          refreshedPhones.find((i) => i.primary) ?? refreshedPhones[0];
        phoneId = refreshed?.id;
        yield put(orderActions.setPhone(refreshed?.number));
        yield put(updatePhones(refreshedPhones));
      }
    }

    if (!phoneId) {
      yield put(orderActions.setPhoneRequired(true));
      yield put(
        mainActions.toggleGlobalLoaderQueue({
          name: 'checkout',
          status: false,
        })
      );
      return;
    }

    const requestBody = {
      client: selectedRestaurant,
      data: {
        mode: type.value,
        phone: phoneId,
        ...getTimeParams(time),
        cart,
      },
    };

    if (type.value === 'delivery' && address && address?.address_id) {
      const deliverAddressString = localStorage.getItem('deliver_address');
      let deliverAddress = null;
      if (deliverAddressString) {
        deliverAddress = safeJsonParse(deliverAddressString);
      }
      requestBody.data.address =
        deliverAddress?.address_id || address.address_id;
    } else if (
      type.value === 'delivery' &&
      address &&
      !address?.address_id &&
      user &&
      address.latitude &&
      address.longitude
    ) {
      try {
        const newAddressResponse = yield call(
          accountApi.address.createAddress,
          {
            address: address.address,
            city: address.city,
            state: address.state,
            zip: address.zipcode,
            latitude: address.latitude,
            longitude: address.longitude,
            primary: false,
          }
        );
        const newAddressId = newAddressResponse?.address?.id;
        if (newAddressId) {
          yield put(
            setSelectedAddress({ ...address, address_id: newAddressId })
          );
          requestBody.data.address = newAddressId;
        }
      } catch (addressErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Checkout] Auto-save delivery address failed (non-blocking):',
          addressErr.message
        );
      }
    }

    if (type.value === 'delivery' && !requestBody.data.address) {
      const fallbackAddr =
        savedAddresses.find((a) => a.primary) || savedAddresses[0];
      if (fallbackAddr) {
        yield put(
          setSelectedAddress({
            entered_address: fallbackAddr.entered_address,
            address: fallbackAddr.address,
            city: fallbackAddr.city,
            state: fallbackAddr.state,
            zipcode: fallbackAddr.zip,
            latitude: fallbackAddr.latitude,
            longitude: fallbackAddr.longitude,
            address_id: fallbackAddr.id,
            primary: fallbackAddr.primary,
            addition: fallbackAddr.addition || null,
          })
        );
        requestBody.data.address = fallbackAddr.id;
      }
    }

    if (type.value === 'delivery' && !requestBody.data.address) {
      yield put(orderActions.setAddressRequired(true));
      yield put(
        mainActions.toggleGlobalLoaderQueue({
          name: 'checkout',
          status: false,
        })
      );
      return;
    }

    const newCheckoutData = yield call(restaurantApiCheckoutStart, requestBody);
    newCheckoutData.restaurant = selectedRestaurant;
    newCheckoutData.noFees = {
      total: newCheckoutData.total,
      subtotal: newCheckoutData.subtotal,
    };

    const dynamicFeesValues = {};
    if (newCheckoutData?.fees) {
      newCheckoutData.fees.forEach((fee) => {
        if ((fee.adjustable || fee.adjustable_percentages) && fee.percent) {
          dynamicFeesValues[fee.id] = fee.percent;
        }
      });
    }

    yield put(orderActions.setDynamicFeesValues(dynamicFeesValues));

    yield put(orderActions.setCheckoutData(newCheckoutData));
    yield put(orderActions.setOrderFees(newCheckoutData.fees));
    yield put(setGCAvailableTrigger(true));
  } catch (err) {
    const errMsg = err?.data?.message || err?.message || '';
    const isNoAddressError = errMsg.toLowerCase().includes('had no address');

    if (isNoAddressError) {
      const {
        retryAddress,
        retryType,
        retryUser,
        retryRestaurant,
        retryCartItems,
        retryTime,
        retryPhone,
        retryPhones,
      } = yield select(
        ({
          address: selectedAddress,
          order,
          main,
          restaurant,
          time: selectedTime,
          profile,
        }) => ({
          retryAddress: selectedAddress.selectedAddress,
          retryType: order.type,
          retryUser: main.user,
          retryRestaurant: restaurant?.selectedRestaurantId,
          retryCartItems: order.cartItems,
          retryTime: selectedTime.selectedTime,
          retryPhone: order.phone,
          retryPhones: profile.phones,
        })
      );

      if (
        retryType?.value === 'delivery' &&
        retryAddress &&
        retryAddress.latitude &&
        retryAddress.longitude
      ) {
        try {
          const retryAddrResponse = yield call(
            accountApi.address.createAddress,
            {
              address: retryAddress.address,
              city: retryAddress.city,
              state: retryAddress.state,
              zip: retryAddress.zipcode,
              latitude: retryAddress.latitude,
              longitude: retryAddress.longitude,
              primary: true,
            }
          );
          const retryAddrId = retryAddrResponse?.address?.id;
          if (retryAddrId) {
            yield put(
              setSelectedAddress({
                ...retryAddress,
                address_id: retryAddrId,
                user_id: retryUser?.id || retryUser?.data?.id,
              })
            );

            let retryPhoneId;
            if (retryPhone) {
              retryPhoneId = retryPhones?.find(
                (i) => i.number === retryPhone
              )?.id;
            }
            if (!retryPhoneId) {
              const fallback =
                retryPhones?.find((i) => i.primary) ?? retryPhones?.[0];
              retryPhoneId = fallback?.id;
            }

            const errData = err?.data || {};
            const existingCheckoutId =
              errData.checkout_id ||
              errData.checkoutId ||
              errData.checkout?.id ||
              null;
            let retryCheckoutData;

            if (existingCheckoutId) {
              retryCheckoutData = yield call(restaurantApiCheckoutAdjust, {
                client: retryRestaurant,
                checkout: existingCheckoutId,
                data: { address: retryAddrId },
              });
              retryCheckoutData = {
                ...retryCheckoutData,
                id: retryCheckoutData.id || existingCheckoutId,
              };
            } else {
              const retryBody = {
                client: retryRestaurant,
                data: {
                  mode: retryType.value,
                  phone: retryPhoneId,
                  ...getTimeParams(retryTime),
                  cart: formatCartForApi(retryCartItems),
                  address: retryAddrId,
                },
              };
              retryCheckoutData = yield call(
                restaurantApiCheckoutStart,
                retryBody
              );
            }
            retryCheckoutData.restaurant = retryRestaurant;
            retryCheckoutData.noFees = {
              total: retryCheckoutData.total,
              subtotal: retryCheckoutData.subtotal,
            };

            const retryDynamicFees = {};
            if (retryCheckoutData?.fees) {
              retryCheckoutData.fees.forEach((fee) => {
                if (
                  (fee.adjustable || fee.adjustable_percentages) &&
                  fee.percent
                ) {
                  retryDynamicFees[fee.id] = fee.percent;
                }
              });
            }

            yield put(orderActions.setDynamicFeesValues(retryDynamicFees));
            yield put(orderActions.setCheckoutData(retryCheckoutData));
            yield put(orderActions.setOrderFees(retryCheckoutData.fees));
            yield put(setGCAvailableTrigger(true));
            return;
          }
        } catch (retryErr) {
          // eslint-disable-next-line no-console
          console.warn(
            '[Checkout] Address recovery retry failed:',
            retryErr?.message
          );
        }

        yield put(orderActions.setAddressRequired(true));
        yield put(
          mainActions.toggleGlobalLoaderQueue({
            name: 'checkout',
            status: false,
          })
        );
        return;
      }

      yield put(orderActions.setAddressRequired(true));
      yield put(
        mainActions.toggleGlobalLoaderQueue({
          name: 'checkout',
          status: false,
        })
      );
      return;
    }

    yield put(orderActions.setCheckoutData(null));

    yield put(
      mainActions.toggleGlobalLoaderQueue({ name: 'checkout', status: false })
    );

    const isMinimumError = errMsg.toLowerCase().includes('minimum');
    if (isMinimumError) {
      Notification.warning(
        'Your cart total does not meet the minimum order amount. Please add more items.',
        { toastId: 'ErrorWhileStartingTheCheckout' }
      );
    } else {
      notifyBugsnag(err);
      Notification.error(errMsg || 'Error while starting the checkout', {
        toastId: 'ErrorWhileStartingTheCheckout',
      });
    }
  }
}

export function* checkoutEnd({ payload }) {
  try {
    const { selectedRestaurant, checkoutData } = yield select(
      ({ order, restaurant }) => ({
        checkoutData: order.checkoutData,
        selectedRestaurant: restaurant.selectedRestaurantId,
      })
    );

    const clientId = selectedRestaurant || checkoutData?.restaurant;

    const requestBody = {
      client: clientId,
      checkout: checkoutData.id,
      payment: payload.payment,
      data: payload.data,
    };

    yield call(restaurantApiCheckoutEnd, requestBody);
    yield call(resetOrderCart, clientId);

    if (payload && payload?.callback) {
      payload.callback();
    }

    yield put(orderActions.setCheckoutData({ ...checkoutData, isEnded: true }));
  } catch (err) {
    notifyBugsnag(err);

    Notification.error(
      err?.data?.message || err?.message || 'Error while ending the checkout',
      {
        toastId: 'ErrorWhileEndingTheCheckout',
      }
    );
  }
}

export function* placeOrder({ payload }) {
  try {
    yield put(
      mainActions.toggleGlobalLoaderQueue({
        name: 'checkout',
        status: true,
      })
    );
    yield put(
      orderActions.checkoutEnd({
        data: {},
        payment: 'cash',
      })
    );

    if (payload.callback) {
      payload.callback();
    }
  } catch (error) {
    payload.showError('Failed to place order.');
  }
}

export function* getExistingAddress(address) {
  try {
    const addresses = yield call(accountApi.address.getAddresses);

    const match = findMatchingAddress(addresses, address.payload);
    yield put(orderActions.setExistingAddress(match || null));
  } catch (error) {
    orderActions.setExistingAddress(error);
  }
}

export function* getNewAddress(address) {
  try {
    const coordinates = yield call(getAddressLocation, address);
    yield put(
      orderActions.setNewAddress({
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        address: address.payload.address,
        city: address.payload.city,
        state: address.payload.state,
        zip: address.payload.zip,
        primary: address.payload.primary,
      })
    );
  } catch (error) {
    orderActions.setNewAddress(error);
  }
}

export function* createNewAddress(action) {
  try {
    const { address, city, state, zipcode, latitude, longitude } =
      action.payload.address;

    const newAddressResponse = yield call(accountApi.address.createAddress, {
      address,
      city,
      state,
      zip: zipcode,
      latitude,
      longitude,
      primary: false,
    });

    // Disabled it can be a different address, not the one currently used, the creator calling it should be the one to do the update
    /*
    const { userId } = action.payload;
    const updatedAddress = {
      entered_address: data.entered_address,
      address: data.address,
      city: data.city,
      state: data.state,
      zipcode,
      latitude: data.latitude,
      longitude: data.longitude,
      primary: false,
      user_id: userId,
      address_id: newAddressResponse.address.id,
    };

    yield put(addressActions.setSelectedAddress(updatedAddress));
    */

    yield put(
      orderActions.setAddressResponse({
        address: newAddressResponse.address,
        orderCase: !!action.payload?.orderCase,
      })
    );
  } catch (error) {
    yield put(orderActions.setAddressResponse(error));
  }
}

export function* getAddressResponse(newAddress) {
  try {
    yield put(
      orderActions.setAddressResponse(
        yield call(accountApi.address.createAddress, newAddress.payload)
      )
    );
  } catch (error) {
    yield put(orderActions.setAddressResponse(error));
  }
}

export function* setCart({ payload }) {
  const { user } = yield select(({ main }) => ({
    user: main.user,
  }));

  try {
    if (user && payload.isCheckoutStart) {
      const orderCart = yield call(setOrderCart, payload.apiData);
      yield put(updateCartStatus(true));
      yield put(setCartResponse({ response: orderCart }));
      if (payload?.adjust) {
        yield call(checkoutAdjustSaga, { payload: {} });
      }
    }
  } catch (err) {
    notifyBugsnag(err);

    let errorMessage = err?.data?.message || err?.message;
    errorMessage = errorMessage?.includes('not found')
      ? errorMessages.ITEM_NOT_AVAILABLE
      : errorMessage;
    yield put(setCartResponse({ error: errorMessage }));

    const { selectedTime } = yield select(({ time }) => ({
      selectedTime: time.selectedTime,
    }));
    const isAsapSelected = selectedTime?.type === 'asap';
    const lowerMsg =
      typeof errorMessage === 'string' ? errorMessage.toLowerCase() : '';
    const isTimeAvailabilityError =
      lowerMsg.includes('not available') &&
      (lowerMsg.includes('date') ||
        lowerMsg.includes('time') ||
        lowerMsg.includes('at selected'));
    if (!isAsapSelected || !isTimeAvailabilityError) {
      Notification.error(errorMessage, {
        toastId: err?.data?.message,
      });
    }

    yield put(updateCartStatus(false));
  } finally {
    yield put(updateCartStatus(false));
    if (payload.payloadCartData?.sort) {
      yield put(
        orderActions.removeUpdatingCartItems(payload.payloadCartData.sort)
      );
    }
  }
}

export function* getCart() {
  try {
    const { user, selectedRestaurant, cartRestaurant, menuItems } =
      yield select(({ main, restaurants, order }) => ({
        user: main.user,
        selectedRestaurant: restaurants.selectedRestaurant,
        cartRestaurant: order.cartRestaurant,
        menuItems: restaurants.menuItems,
      }));

    if (cartRestaurant !== selectedRestaurant) {
      yield put(orderActions.setDynamicFeesValues({}));
      yield call(setCart, { payloadCartData: { cart: [] } });
      return;
    }

    let cartFromApi = [];
    if (user) {
      const response = yield call(getOrderCart, selectedRestaurant);
      cartFromApi = formatCartForStore(response?.cart || [], menuItems);
    }

    yield put(orderActions.setCartItems(cartFromApi));
  } catch (err) {
    notifyBugsnag(err);

    Notification.error(
      err?.data?.message || err?.message || 'Error while getting the cart',
      {
        toastId: 'ErrorWhileGettingTheCart',
      }
    );
  }
}

export function* createOptionalAddress({ payload }) {
  try {
    yield call(accountApi.address.updateAddress, payload.id, payload.data);
  } catch (error) {
    if (payload.showError) {
      payload.showError('Failed to update address.');
    }
  }
}

export function* watchOrderSagas() {
  yield takeLeading(orderActions.getCart.type, getCart);
  yield takeLeading(orderActions.setCart.type, setCart);
  yield takeLeading(orderActions.checkoutStart.type, checkoutStart);
  yield takeLeading(
    orderActions.setCheckoutAdjustRequest.type,
    checkoutAdjustSaga
  );
  yield takeLeading(orderActions.checkoutEnd.type, checkoutEnd);
  yield takeLeading(orderActions.placeOrder.type, placeOrder);
  yield takeLeading(orderActions.getExistingAddress.type, getExistingAddress);
  yield takeLeading(orderActions.getNewAddressCoordinates.type, getNewAddress);
  yield takeLeading(orderActions.createNewAddress.type, createNewAddress);
  yield takeLeading(
    orderActions.getNewAddressResponse.type,
    getAddressResponse
  );
  yield takeLeading(
    orderActions.createOptionalAddress.type,
    createOptionalAddress
  );
}
